import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'
import { SignatureFieldType } from './signService'

export type DocumentCategory = 'client' | 'tenant';

export interface MatterDocument {
  id: string;
  matterId: string;
  tenantId: string;
  category: DocumentCategory;
  s3Key: string;
  fileName: string;
  contentType: string;
  uploadedBy: 'tenant' | 'client';
  uploadLinkId?: string | null;
  docType?: string | null;
  createdAt: string;
  // Present when this document was produced by generate-document (see
  // documentTemplateService.ts) rather than uploaded — signatureFields are
  // pre-positioned per party role (from the template's execution page), so
  // "Send for Signature" can skip manual field placement for these.
  sourceTemplateId?: string | null;
  partyRoles?: string[] | null;
  signatureFields?: { id: string; type: SignatureFieldType; page: number; xPct: number; yPct: number; widthPct: number; heightPct: number }[];
  [key: string]: unknown;
}

export interface VicReportsStatus {
  status: 'pending' | 'complete' | 'failed' | null;
  error: string | null;
  resolvedAddress: string | null;
  manualFetchUrl: string;
}

export interface PepFepCheckResult {
  clientId: string;
  clientName: string;
  documentId?: string;
  error?: string;
}

export interface PepFepCheckStatus {
  status: 'pending' | 'complete' | 'failed' | null;
  error: string | null;
  results: PepFepCheckResult[] | null;
}

// Uploads go straight to S3 via a presigned URL rather than through the
// Lambda, to avoid API Gateway/Lambda payload size limits — the backend only
// ever issues the URL and records metadata, it never sees file bytes.
const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`)
  }
}

// Authenticated: tenant-side document access on a matter they own.
export const useDocumentService = () => {
  const { token, authFetch } = useAuth()

  const listDocuments = async (matterId: string): Promise<MatterDocument[]> => {
    if (!token) {
      return []
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/documents`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list documents: ${response.status}`)
    }

    return data.documents || []
  }

  const uploadDocument = async (matterId: string, file: File, category: DocumentCategory = 'tenant'): Promise<MatterDocument> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream', category }),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to start upload: ${response.status}`)
    }

    await uploadFileToS3(data.uploadUrl, file)

    return data.document
  }

  const getDownloadUrl = async (matterId: string, documentId: string): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(
      `${API_BASE_URL}/matters/${matterId}/documents/${documentId}/download-url`,
    )
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get download link: ${response.status}`)
    }

    return data.downloadUrl
  }

  const getUploadLink = async (matterId: string): Promise<{ token: string; expiresAt: string }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/upload-link`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get upload link: ${response.status}`)
    }

    return data
  }

  // One link per (matter, client) pair — see getOrCreatePortalLinkForClient
  // in backend/src/index.ts. Covers checklist viewing, document upload, and
  // starting a Didit ID check, all from one link (unlike getUploadLink
  // above, which is upload-only and shared across every client on a matter).
  const getPortalLink = async (matterId: string, clientId: string): Promise<{ token: string; expiresAt: string }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/clients/${clientId}/portal-link`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get portal link: ${response.status}`)
    }

    return data
  }

  // Best-effort: this only enqueues the fetch (the backend does the actual
  // work — a Cloudflare challenge + remote-browser round trip that can take
  // over a minute — in a separate worker, see
  // backend/src/vic-property-reports-worker.ts) and returns immediately.
  // Poll getVicReportsStatus below for the outcome. Land.vic.gov.au's
  // Cloudflare protection blocking the fetch is a routine, expected failure
  // mode, not a bug — callers should treat a 'failed' status as "show the
  // manual-fetch instructions", not an error to report.
  const fetchGovernmentReports = async (matterId: string): Promise<void> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/vic-property-reports`, {
      method: 'POST',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new Error(data?.error || `Failed to start property report fetch: ${response.status}`)
    }
  }

  const getVicReportsStatus = async (matterId: string): Promise<VicReportsStatus> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/vic-property-reports/status`)

    // A Gateway-level timeout/error can come back as plain text/HTML rather
    // than JSON, so this can't assume response.json() will succeed even when
    // !response.ok.
    let data: any = null
    try {
      data = await response.json()
    } catch {
      // fall through to the generic error below
    }

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get property report status: ${response.status}`)
    }

    return data
  }

  // Best-effort, same shape as fetchGovernmentReports above: this only
  // enqueues the screening (see backend/src/pep-fep-check-worker.ts) and
  // returns immediately. Poll getPepFepCheckStatus for the outcome.
  const fetchPepFepCheck = async (matterId: string): Promise<void> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/pep-fep-check`, {
      method: 'POST',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new Error(data?.error || `Failed to start PEP/FEP check: ${response.status}`)
    }
  }

  const getPepFepCheckStatus = async (matterId: string): Promise<PepFepCheckStatus> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/pep-fep-check/status`)

    // A Gateway-level timeout/error can come back as plain text/HTML rather
    // than JSON, so this can't assume response.json() will succeed even when
    // !response.ok.
    let data: any = null
    try {
      data = await response.json()
    } catch {
      // fall through to the generic error below
    }

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get PEP/FEP check status: ${response.status}`)
    }

    return data
  }

  return {
    listDocuments,
    uploadDocument,
    getDownloadUrl,
    getUploadLink,
    getPortalLink,
    fetchGovernmentReports,
    getVicReportsStatus,
    fetchPepFepCheck,
    getPepFepCheckStatus,
  }
}
